import React, { useState, useCallback, useMemo, useEffect } from 'react';

import { debounce } from '@mui/material/utils';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import type { AutocompleteProps } from '@mui/material/Autocomplete';
import Tooltip from '@/components/mui/Tooltip';
import { AutocompleteStyled } from '@/pages/company/DashboardRoute/pages/VendorExpensesRoute/components/Autocomplete/StyledComponents';

import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ClearIcon from '@mui/icons-material/Clear';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const passthroughFilter = <T,>(options: T[]): T[] => options;

type MuiAutocompleteProps<T> = AutocompleteProps<T, true, boolean, false>;

export type AutocompleteAsyncWithCheckboxesProps<T> = Partial<
  Omit<MuiAutocompleteProps<T>, 'value' | 'onChange' | 'options' | 'renderInput'>
> & {
  fetchOptions: (searchText?: string) => Promise<T[]>;
  value?: T[];
  onChange: (event: React.SyntheticEvent | null, value: T[]) => void;
  label?: React.ReactNode;
  valueKey?: string;
  labelKey?: string;
  debounceTime?: number;
};

const AutocompleteAsyncWithCheckboxes = <T extends Record<string, any>>({
  fetchOptions,
  value = [],
  onChange,
  label,
  valueKey = 'id',
  labelKey = 'label',
  debounceTime = 500,
  ...autocompleteProps
}: AutocompleteAsyncWithCheckboxesProps<T>) => {
  const [options, setOptions] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [internalValue, setInternalValue] = useState<T[]>(value);

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  const handleFetchOptions = useCallback(
    async (searchText: string) => {
      setLoading(true);
      try {
        const newOptions = await fetchOptions(searchText);

        setOptions(newOptions || []);
      } catch (error) {
        console.error('Error fetching options:', error);
      } finally {
        setLoading(false);
      }
    },
    [fetchOptions],
  );

  const debouncedFetch = useMemo(
    () =>
      debounce((searchText: string) => {
        handleFetchOptions(searchText);
      }, debounceTime),
    [handleFetchOptions, debounceTime],
  );

  const handleInputChange = useCallback<NonNullable<MuiAutocompleteProps<T>['onInputChange']>>(
    (_event, newValue, reason) => {
      if (reason === 'input') {
        setInputValue(newValue);
        if (newValue.length >= 2) {
          debouncedFetch(newValue);
        } else {
          debouncedFetch('');
        }
      } else if (reason === 'clear') {
        setInputValue('');
        setInternalValue([]);
        debouncedFetch('');
        onChange(null, []);
      }
    },
    [debouncedFetch, onChange],
  );

  const handleSelectionChange = useCallback<NonNullable<MuiAutocompleteProps<T>['onChange']>>((_event, newValue) => {
    setInternalValue(newValue || []);
  }, []);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    handleFetchOptions(inputValue);
  }, [handleFetchOptions, inputValue]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setInputValue('');

    if (onChange) {
      onChange(null, internalValue);
    }
  }, [onChange, internalValue]);

  const renderValue = useCallback<NonNullable<MuiAutocompleteProps<T>['renderValue']>>(
    (tagValue, getItemProps) => {
      if (isOpen) {
        return null;
      }

      if (tagValue.length === 0) return null;

      const firstTag = tagValue[0];
      const remainingCount = tagValue.length - 1;
      const { onDelete, ...tagProps } = getItemProps({ index: 0 });

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap', minWidth: 0 }}>
          <Chip {...tagProps} label={firstTag[labelKey] || firstTag[valueKey]} size="small" sx={{ minWidth: 0 }} />
          {remainingCount > 0 && (
            <Tooltip
              t={
                tagValue.length > 1 ? (
                  <Box>
                    {tagValue.map((item, index) => (
                      <Box key={index} sx={{ fontSize: '0.75rem', py: 0.25 }}>
                        {item[labelKey] || item[valueKey]}
                      </Box>
                    ))}
                  </Box>
                ) : null
              }
              arrow
              placement="bottom-start"
              disabled={tagValue.length <= 1}
            >
              <Chip label={`+${remainingCount}`} size="small" />
            </Tooltip>
          )}
        </Box>
      );
    },
    [isOpen, labelKey, valueKey],
  );

  const renderOption = useCallback<NonNullable<MuiAutocompleteProps<T>['renderOption']>>(
    (props, option, { selected }) => {
      const { key, ...liProps } = props;
      return (
        <li key={key} {...liProps}>
          <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8, padding: 0 }} checked={selected} />
          {option[labelKey] || option[valueKey]}
        </li>
      );
    },
    [labelKey, valueKey],
  );

  const getOptionLabel = useCallback(
    (option: T) => {
      return option[labelKey] || option[valueKey] || '';
    },
    [labelKey, valueKey],
  );

  const getOptionKey = useCallback(
    (option: T) => {
      return option[valueKey];
    },
    [valueKey],
  );

  const isOptionEqualToValue = useCallback(
    (option: T, v: T) => {
      const optionValue = option[valueKey];
      const compareValue = v[valueKey];
      return optionValue === compareValue;
    },
    [valueKey],
  );

  const clearSelectedValues = useCallback(() => {
    setInternalValue([]);
    onChange(null, []);
  }, [onChange]);

  return (
    <AutocompleteStyled<T, true, boolean, false>
      multiple
      disableCloseOnSelect
      open={isOpen}
      onOpen={handleOpen}
      onClose={handleClose}
      options={options}
      value={internalValue}
      onChange={handleSelectionChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      getOptionLabel={getOptionLabel}
      getOptionKey={getOptionKey}
      isOptionEqualToValue={isOptionEqualToValue}
      renderOption={renderOption}
      renderValue={renderValue}
      loading={loading}
      filterOptions={passthroughFilter}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {internalValue.length > 0 && !isOpen && (
                    <IconButton size="small" onClick={clearSelectedValues}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )}
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      {...autocompleteProps}
    />
  );
};

export default AutocompleteAsyncWithCheckboxes;
